import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePreferences } from '../contexts/PreferencesContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import { get, del, ApiError } from 'aws-amplify/api';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RestaurantIcon from '@mui/icons-material/Restaurant';

interface FoodItem {
  name: string;
  calories: number;
}

interface Meal {
  userId: string;
  mealTimestamp: string;
  date: string;
  timestamp: number;
  totalCalories: number;
  confidenceLevel: string;
  foodItems: FoodItem[];
  imageKey?: string;
  dishName?: string;
}

interface PaginatedResponse {
  meals: Meal[];
  nextToken?: string;
}

const MealHistory: React.FC = () => {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const observerTarget = React.useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterDate, setFilterDate] = useState<string>(
    searchParams.get('date') || new Date().toISOString().split('T')[0]
  );
  const [currentDate, setCurrentDate] = useState<Date>(
    searchParams.get('date') ? new Date(searchParams.get('date')!) : new Date()
  );

  const [slidingMeal, setSlidingMeal] = useState<string | null>(null);
  const [startX, setStartX] = useState<number>(0);
  const [currentX, setCurrentX] = useState<number>(0);
  const [weekMeals, setWeekMeals] = useState<Meal[]>([]);
  const navigate = useNavigate();
  const { preferences } = usePreferences();

  const fetchMeals = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get the current session
      const session = (await fetchAuthSession()) ?? {};        console.log('ldskjglkjg');
      const authToken = session.tokens?.idToken?.toString();

      if (!authToken) {
        throw new Error('Not authenticated');
      }

      const options: { queryParams?: Record<string, string>, headers?: Record<string, string> } = {
        headers: { Authorization: authToken }
      };

      const queryParams: Record<string, string> = {
        limit: '40'
      };
      if (filterDate) {
        queryParams.date = filterDate;
      }
      if (nextToken && !loading) {
        queryParams.nextToken = nextToken;
      }
      options.queryParams = queryParams;

      const restOperation = get({
        apiName: 'FoodTrackerAPI',
        path: '/food/meals',
        options
      });

      const { body } = await restOperation.response;
      const data = await body.json() as unknown;
      const response = data as PaginatedResponse;
      
      if (response && 'meals' in response) {
        const sortedMeals = response.meals.sort((a, b) => b.timestamp - a.timestamp);
        setMeals(prev => loading ? sortedMeals : [...prev, ...sortedMeals]);
        setNextToken(response.nextToken);
        setHasMore(!!response.nextToken);
      } else {
        setMeals([]);
      }
    } catch (error) {
      console.error('Error fetching meals:', error);
      if (error instanceof ApiError) {
        if (error.response) {
          const { statusCode } = error.response;
          if (statusCode === 401) {
            setError('Authentication error. Please sign out and sign in again.');
          } else if (statusCode === 204) {
            setMeals([]);
          } else {
            setError('Failed to load your meal history. Please try again.');
          }
        }
      } else {
        setError('Failed to load your meal history. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [filterDate, nextToken, loading]);

  // Fetch week meals for calendar indicators
  const fetchWeekMeals = useCallback(async () => {
    try {
      const session = (await fetchAuthSession()) ?? {};
      const authToken = session.tokens?.idToken?.toString();

      if (!authToken) {
        return;
      }

      const options = {
        headers: { Authorization: authToken },
        queryParams: {
          limit: '200' // Get more meals to cover recent history
        }
      };

      const restOperation = get({
        apiName: 'FoodTrackerAPI',
        path: '/food/meals',
        options
      });

      const { body } = await restOperation.response;
      const data = await body.json() as unknown;
      const response = data as PaginatedResponse;
      
      if (response && 'meals' in response) {
        setWeekMeals(response.meals);
      }
    } catch (error) {
      console.error('Error fetching week meals:', error);
    }
  }, []);

  useEffect(() => {
    setMeals([]);
    setNextToken(undefined);
    setHasMore(true);
    fetchMeals();
  }, [filterDate]);

  // Fetch week meals on component mount
  useEffect(() => {
    fetchWeekMeals();
  }, []);

  // Set URL params on initial load if no date is specified
  useEffect(() => {
    if (!searchParams.get('date')) {
      const today = new Date().toISOString().split('T')[0];
      setSearchParams({ date: today });
    }
  }, [searchParams, setSearchParams]);

  // Intersection Observer setup
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          setLoadingMore(true);
          fetchMeals().finally(() => setLoadingMore(false));
        }
      },
      { threshold: 1.0 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, fetchMeals]);



  // Group meals by date and calculate daily totals
  const mealsByDate = meals.reduce((groups: {[key: string]: { meals: Meal[], totalCalories: number }}, meal) => {
    const date = meal.date;
    if (!groups[date]) {
      groups[date] = { meals: [], totalCalories: 0 };
    }
    groups[date].meals.push(meal);
    groups[date].totalCalories += meal.totalCalories;
    return groups;
  }, {});

  const getDailyCalorieClass = (totalCalories: number): string => {
    if (totalCalories > preferences.dailyCalorieTarget) {
      return 'calories-over';
    }
    return 'calories-under';
  };

  // Format time
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get meal icon - simple restaurant icon for all meals
  const getMealIcon = () => {
    return <RestaurantIcon className="meal-icon" />;
  };

  // Navigate calendar by week
  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentDate(newDate);
    const dateStr = newDate.toISOString().split('T')[0];
    setFilterDate(dateStr);
    setSearchParams({ date: dateStr });
  };

  // Format date for display in English
  const formatDateEnglish = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('en-US', options);
  };

  // Get week days for calendar
  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    startOfWeek.setDate(diff);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const weekDays = getWeekDays();
  const dayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  const handleSlideEnd = async (meal: Meal) => {
    const diff = currentX - startX;
    if (diff < -40) {
      // Confirm delete
      if (window.confirm('Are you sure you want to delete this meal?')) {
        try {
          const session = await fetchAuthSession();
          const authToken = session.tokens?.idToken?.toString();
          
          if (!authToken) {
            throw new Error('Not authenticated');
          }
          
          await del({
            apiName: 'FoodTrackerAPI',
            path: `/food/meals/${encodeURIComponent(meal.mealTimestamp || '')}`,
            options: {
              headers: { Authorization: authToken }
            }
          });

          // Remove the meal from state
          setMeals(prevMeals => prevMeals.filter(m => m.mealTimestamp !== meal.mealTimestamp));
        } catch (error) {
          console.error('Error deleting meal:', error);
          alert('Failed to delete meal. Please try again.');
        }
      }
    }
    setSlidingMeal(null);
    setStartX(0);
    setCurrentX(0);
  };

  if (loading && meals.length === 0) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="meal-history-container">
      {/* Calendar Header */}
      <div className="calendar-header">
        <div className="calendar-navigation">
          <button className="nav-arrow" onClick={() => navigateDate('prev')}>
            <ChevronLeftIcon />
          </button>
          <h2 className="current-date">{formatDateEnglish(currentDate)}</h2>
          <button className="nav-arrow" onClick={() => navigateDate('next')}>
            <ChevronRightIcon />
          </button>
        </div>
        
        {/* Week Calendar */}
        <div className="week-calendar">
          {weekDays.map((day, index) => {
            const isToday = day.toDateString() === new Date().toDateString();
            const isSelected = day.toDateString() === currentDate.toDateString();
            const dayStr = day.toISOString().split('T')[0];
            const hasData = weekMeals.some(meal => meal.date === dayStr);
            
            return (
              <div 
                key={index}
                className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${hasData ? 'has-data' : ''}`}
                onClick={() => {
                  setCurrentDate(day);
                  const dateStr = day.toISOString().split('T')[0];
                  setFilterDate(dateStr);
                  setSearchParams({ date: dateStr });
                }}
              >
                <span className="day-label">{dayLabels[index]}</span>
                <span className="day-number">{day.getDate()}</span>
                <div className={`data-indicator ${hasData ? 'visible' : 'hidden'}`}></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      <div className="meals-content">
        {error && <div className="error-message">{error}</div>}
        
        {meals.length === 0 && !loading ? (
          <div className="empty-state">
            <div className="empty-icon">🍽️</div>
            <h3>No meals found</h3>
            <p>Start tracking your meals by taking photos of your food!</p>
          </div>
        ) : (
          <div className="meals-list">
            {/* Daily Summary */}
            {filterDate && mealsByDate[filterDate] && (
              <div className="daily-summary">
                <div className="summary-header">
                  <h3>Daily Summary</h3>
                  <div className={`total-calories ${getDailyCalorieClass(mealsByDate[filterDate].totalCalories)}`}>
                    {mealsByDate[filterDate].totalCalories} kcal
                  </div>
                </div>
                <div className="nutrition-overview">
                  <div className="nutrition-item">
                    <span className="nutrition-label">Meals</span>
                    <span className="nutrition-value">{mealsByDate[filterDate].meals.length}</span>
                  </div>
                  <div className="nutrition-item">
                    <span className="nutrition-label">Target</span>
                    <span className="nutrition-value">{preferences.dailyCalorieTarget}</span>
                  </div>
                  <div className="nutrition-item">
                    <span className="nutrition-label">Remaining</span>
                    <span className="nutrition-value">
                      {Math.max(0, preferences.dailyCalorieTarget - mealsByDate[filterDate].totalCalories)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Meals */}
            {Object.entries(mealsByDate)
              .filter(([date]) => !filterDate || date === filterDate)
              .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
              .map(([date, { meals: dayMeals }]) => (
                <div key={date} className="day-meals">
                  {dayMeals
                    .sort((a, b) => a.timestamp - b.timestamp)
                    .map((meal) => (
                    <div 
                      key={meal.mealTimestamp} 
                      className={`meal-card ${slidingMeal === meal.mealTimestamp ? 'sliding' : ''}`}
                      onTouchStart={(e) => {
                        setStartX(e.touches[0].clientX);
                        setCurrentX(e.touches[0].clientX);
                      }}
                      onTouchMove={(e) => {
                        const x = e.touches[0].clientX;
                        const diff = x - startX;
                        if (diff < 0 && diff > -80) {
                          setCurrentX(x);
                          setSlidingMeal(meal.mealTimestamp);
                        }
                      }}
                      onTouchEnd={() => handleSlideEnd(meal)}
                    >
                      <div 
                        className="meal-card-content"
                        onClick={() => navigate(`/analysis`, { state: { mealData: meal } })}
                      >
                        <div className="meal-header">
                          {getMealIcon()}
                          <div className="meal-info">
                            <h4 className="meal-title">
                              {meal.dishName || (
                                meal.foodItems.slice(0, 2).map(item => item.name).join(', ') +
                                (meal.foodItems.length > 2 ? '...' : '')
                              )}
                            </h4>
                            <span className="meal-time">{formatTime(meal.timestamp)}</span>
                          </div>
                          <div className="meal-calories">
                            <span className="calories-number">{meal.totalCalories}</span>
                            <span className="calories-label">Calories</span>
                          </div>
                        </div>
                        
                        <div className="meal-confidence">
                          <span className={`confidence-badge ${meal.confidenceLevel.toLowerCase()}`}>
                            Confidence: {meal.confidenceLevel}
                          </span>
                        </div>
                      </div>
                      <div className="meal-delete-action">Delete</div>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}
        
        {(loadingMore || (loading && meals.length > 0)) && (
          <div className="loading-more">
            <div className="loading-spinner"></div>
          </div>
        )}
        <div ref={observerTarget} style={{ height: '20px' }}></div>
      </div>
    </div>
  );
};

export default MealHistory;
